import {useMemo, useCallback, useEffect} from "react";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";

const GEO_COORDINATES_REGEXP = /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i;

export const useMapboxGeolocator = (zoom: number, t: (text: string) => string) => {
    const lattitude = useMemo(() => t("enumerations.lattitude"), [t]);
    const longitude = useMemo(() => t("enumerations.longitude"), [t]);
    const placeholder = useMemo(() => t("fields.field_draw_map.coord_placeholder"), [t]);

    const coordinatesGeocoder = useCallback(
        (query: string): MapboxGeocoder.Result[] => {
            const matches = GEO_COORDINATES_REGEXP.exec(query);
            if (!matches) {
                return [];
            }

            const coordinateFeature = (lng: number, lat: number) =>
                ({
                    center: [lng, lat],
                    geometry: {type: "Point", coordinates: [lng, lat]},
                    place_name: `${lattitude}: ${lat} ${longitude}: ${lng}`,
                    place_type: ["coordinate"],
                    properties: {},
                    type: "Feature",
                }) as MapboxGeocoder.Result;

            const coord1 = Number(matches[1]);
            const coord2 = Number(matches[2]);
            const geocodes = [];

            if (coord1 < -90 || coord1 > 90) {
                geocodes.push(coordinateFeature(coord1, coord2));
            }

            if (coord2 < -90 || coord2 > 90) {
                geocodes.push(coordinateFeature(coord2, coord1));
            }

            if (geocodes.length === 0) {
                geocodes.push(coordinateFeature(coord1, coord2));
                geocodes.push(coordinateFeature(coord2, coord1));
            }

            return geocodes;
        },
        [lattitude, longitude],
    );

    const mapboxGeolocator = useMemo(() => {
        const options = {
            accessToken: "",
            localGeocoder: coordinatesGeocoder,
            localGeocoderOnly: false,
            zoom: zoom,
            placeholder: placeholder,
            reverseGeocode: true,
            marker: false,
            flyTo: {
                speed: 3,
                maxDuration: 50,
            },
        };

        return new MapboxGeocoder(options);
    }, [zoom, placeholder, coordinatesGeocoder]);

    useEffect(() => {
        if (mapboxGeolocator) {
            mapboxGeolocator.setZoom(zoom);
        }
    }, [mapboxGeolocator, zoom]);

    return mapboxGeolocator;
};
